<template>
  <div class="human-resources-kpi pa-4"
    :style="themeStyles"
    :data-theme-dark="isDark"
  >
    <div class="d-flex align-center mb-3">
      <h3 class="ma-0 text-h6">HUMAN RESOURCES</h3>
      <v-spacer></v-spacer>
      <v-chip small>{{ projectHealthLabel }}</v-chip>
    </div>

    <div class="kpi-table-wrapper">
      <div class="table-container">
        <table class="kpi-table main-table">
          <thead>
            <tr>
              <th class="applicability text-center">Applicability</th>
              <th class="selector text-center">Selector</th>
              <th class="baseline text-center">Baseline</th>
              <th class="current-month">Current Month</th>
              <th class="label-dropdown text-center">Employee Label</th>
              <th class="employees text-center">Employees on Project</th>
              <th
                v-for="(col, i) in dynamicColumns"
                :key="i"
                class="text-right period-col"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in kpiData" :key="item.KPI_ID || index">
              <td class="text-center applicability">
                <div class="applicability-checkbox">
                  <v-checkbox
                    v-model="item.Is_Active"
                    hide-details
                    dense
                    @change="onApplicabilityChange(item)"
                  ></v-checkbox>
                </div>
              </td>

              <td class="selector">
                <v-text-field
                  v-model="item.Selector_Name"
                  dense
                  hide-details
                  :disabled="!item.Is_Active"
                  readonly
                ></v-text-field>
              </td>

              <td class="baseline">
                <div class="text-right">
                  <v-text-field
                    v-model.number="item.Baseline"
                    type="number"
                    dense
                    hide-details
                    suffix="%"
                    :disabled="!item.Is_Active"
                    @input="markAsChanged"
                  ></v-text-field>
                </div>
              </td>

              <td class="current-month">
                <div class="current-month-wrapper d-flex align-center justify-end">
                  <DatePicker
                    v-model="item.CurrentMonthPicker"
                    type="month"
                    label=""
                    class="date-picker-inline"
                    :dense="true"
                    :hide-details="true"
                    :disabled="!item.Is_Active"
                    @onDatePickerChange="() => onMonthChange(item)"
                  />
                  <span
                    class="ml-2"
                    :class="{ 'grey--text': shouldShowNA(item) || !item.Is_Active }"
                  >
                    {{ displayCurrentMonth(item) }}
                  </span>
                </div>
              </td>

              <td class="label-dropdown">
                <div class="text-center">
                  <v-select
                    v-model="item.Selected_Label_ID"
                    :items="employeeLabels"
                    item-text="Label_Name"
                    item-value="Label_ID"
                    dense
                    hide-details
                    :disabled="!item.Is_Active"
                    placeholder="Select"
                    @change="onLabelChange(item)"
                  ></v-select>
                </div>
              </td>

              <td class="employees">
                <div class="text-right">
                  <v-text-field
                    v-model.number="item.EmployeesOnProject"
                    dense
                    hide-details
                    :disabled="!item.Is_Active"
                    readonly
                    class="text-right-field"
                    @input="markAsChanged"
                  ></v-text-field>
                </div>
              </td>

              <td
                v-for="(col, i) in dynamicColumns"
                :key="i"
                class="period-col text-right"
              >
                <div class="text-right">
                  <v-text-field
                    v-model.number="item[col]"
                    dense
                    hide-details
                    :disabled="true"
                    class="text-right-field"
                  ></v-text-field>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="action-bar d-flex justify-end">
      <v-btn
        small
        color="primary"
        outlined
        class="mr-3"
        @click.prevent="exportToXLSX"
      >
        Export To XLSX
      </v-btn>

      <v-btn
        color="primary"
        @click="saveKpiData"
        :loading="saving"
        small
        :disabled="!canSave"
      >
        <v-icon left>mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/global/DatePicker.vue'
const xlsx = require('json-as-xlsx')

export default {
  name: 'HumanResourcesKPI',
  components: { DatePicker },
  props: { kpiType: { type: String, required: true } },
  data () {
    return {
      kpiData: [],
      dynamicColumns: [],
      saving: false,
      projectStatus: 'FAIR',
      hasChanges: false,
      exporting: false,
      employeeLabels: []
    }
  },
  computed: {
    projectHealth () {
      if (!this.kpiData.length) return 0

      const activeItems = this.kpiData.filter(item => item.Is_Active)
      if (!activeItems.length) return 0

      const sum = activeItems.reduce((total, item) => {
        const baseline = parseFloat(item.Baseline) || 0
        return total + baseline
      }, 0)

      const average = sum / activeItems.length
      return Math.round(average)
    },
    projectHealthLabel () {
      return `Project Health (${this.projectHealth}%): ${this.projectStatus}`
    },
    user () {
      return this.$store.getters.getUser || {}
    },
    selectedProject () {
      return this.$store.getters.getSelectedProject
    },
    selectedProjectName () {
      return this.$store.getters.getSelectedProjectName
    },
    canSave () {
      if (!this.kpiData.length) return false

      const changed = this.hasChanges

      const validRowExists = this.kpiData.some(item =>
        item.Is_Active &&
        item.CurrentMonthPicker &&
        item.Selected_Label_ID
      )

      return changed && validRowExists
    },
    isDark () {
      return this.$vuetify.theme.dark
    },
    themeStyles () {
      return {
        '--hr-bg': this.isDark ? '#1e1e1e' : '#ffffff',
        '--hr-bg-header': this.isDark ? '#2a2a2a' : '#f5f5f5',
        '--hr-border': this.isDark ? '#444' : '#ddd',
        '--hr-text': this.isDark ? '#e0e0e0' : '#000000',
        '--hr-text-muted': this.isDark ? '#999' : '#555'
      }
    }
  },
  watch: {
    selectedProject: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.loadEmployeeLabels()
          this.loadKpiData(newVal)
        }
      }
    }
  },
  methods: {
    markAsChanged () {
      this.hasChanges = true
    },

    async loadKpiData (projectId) {
      this.addKPISelectors(projectId)
      try {
        const res = await this.axios.get(`/api/kpi_periods/${projectId}`, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })

        const rows = res.data || []
        this.getLabels()
        if (!rows.length) {
          this.kpiData = []
          this.dynamicColumns = []
          return
        }

        const allKeys = Object.keys(rows[0])
        const periodColumns = allKeys.filter(k => /^[A-Za-z]{3}\s\d{4}$/.test(k))
        this.dynamicColumns = periodColumns
        this.kpiData = rows.map(row => ({
          ...row,
          CurrentMonthPicker: row.Period ? new Date(row.Period) : null,
          EmployeesOnProject: row.EmployeesOnProject || 0,
          Is_Active: row.Is_Active,
          Baseline: row.Baseline || 0,
          Selected_Label_ID: row.Selected_Label_ID || null
        }))
        this.hasChanges = false
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: err.message + ' - ' + (err.response?.data || '')
        })
      }
    },

    async loadEmployeeLabels () {
      try {
        const res = await this.axios.get('/api/project_employee_labels', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.employeeLabels = res.data || []
      } catch (error) {
        console.error('Error loading employee labels:', error)
        this.$store.dispatch('setError', {
          errorMsg: error.message + ' - ' + (error.response?.data || '')
        })
      }
    },

    onLabelChange (item) {
      this.markAsChanged()
      if (item.Is_Active && item.CurrentMonthPicker) {
        this.onMonthChange(item)
      }
    },

    async addKPISelectors (projectId) {
      try {
        await this.axios.post('/api/add_kpi_selectors', {
          Project_ID: projectId
        }, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: 'Error adding KPI selectors: ' + err.message + ' - ' + (err.response?.data || '')
        })
      }
    },

    displayCurrentMonth (item) {
      if (item.EmployeesOnProject === 0 || item.Current_Month === null || isNaN(item.Current_Month)) {
        return 'N/A'
      }
      return parseFloat(item.Current_Month).toFixed(2)
    },

    shouldShowNA (item) {
      return (
        item.EmployeesOnProject === 0 || item.Current_Month === null || isNaN(item.Current_Month)
      )
    },

    async onApplicabilityChange (item) {
      this.markAsChanged()
      if (item.Is_Active && item.CurrentMonthPicker) {
        await this.onMonthChange(item)
      }
    },

    async onMonthChange (item) {
      if (!item.Is_Active || !item.CurrentMonthPicker || !this.selectedProject) return
      try {
        const res = await this.axios.get('/api/kpi_value', {
          headers: { Authorization: 'Bearer ' + this.user.token },
          params: {
            Project_ID: this.selectedProject,
            Label_ID: item.Selected_Label_ID,
            Period: item.CurrentMonthPicker
          }
        })

        const data = res.data?.[0]
        if (data) {
          item.Current_Month = data.Value
          item.EmployeesOnProject = data.EmployeeCount
        } else {
          item.Current_Month = null
          item.EmployeesOnProject = null
        }
        this.markAsChanged()
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: err.message + ' - ' + (err.response?.data || '')
        })
      }
    },

    async saveKpiData () {
      this.saving = true
      try {
        const payload = this.kpiData.map(i => {
          let period = null
          if (i.CurrentMonthPicker) {
            if (i.CurrentMonthPicker instanceof Date) {
              period = i.CurrentMonthPicker.toISOString().slice(0, 7)
            } else if (typeof i.CurrentMonthPicker === 'string') {
              const parsed = new Date(i.CurrentMonthPicker)
              period = !isNaN(parsed)
                ? parsed.toISOString().slice(0, 7)
                : new Date().toISOString().slice(0, 7)
            }
          } else {
            period = new Date().toISOString().slice(0, 7)
          }

          if (period !== new Date().toISOString().slice(0, 7)) {
            this.$store.dispatch('setError', {
              errorMsg: 'Previous period cannot be recalculated.'
            })
            return null
          }

          return {
            Project_ID: this.selectedProject,
            KPI_Selector_ID: i.KPI_Selector_ID,
            KPI_Type_ID: i.KPI_Type_ID || 4,
            Baseline: i.Baseline || 0,
            Current_Month: i.Current_Month || 0,
            Period: period,
            Value: i.Current_Month || 0,
            Is_Active: i.Is_Active ? 1 : 0,
            Selected_Label_ID: i.Selected_Label_ID || null
          }
        }).filter(item => item !== null)

        if (payload.length === 0) {
          this.$store.dispatch('setError', {
            errorMsg: 'No valid data to save.'
          })
          return
        }

        await this.axios.post('/api/kpi/save', payload, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.hasChanges = false
        await this.loadKpiData(this.selectedProject)
        this.$emit('saved')
      } catch (err) {
        this.$store.dispatch('setError', {
          errorMsg: err.message + ' - ' + (err.response?.data || '')
        })
      } finally {
        this.saving = false
      }
    },

    async exportToXLSX () {
      if (!this.kpiData.length) {
        this.$store.dispatch('setError', {
          errorMsg: 'No data to export'
        })
        return
      }

      this.exporting = true

      try {
        const projectRow = {
          Project: `${this.selectedProjectName || 'N/A'}`
        }

        const columns = [
          { label: 'Project:', value: 'Project' },
          { label: 'Applicability', value: 'Applicability' },
          { label: 'Selector', value: 'Selector' },
          { label: 'Baseline', value: 'Baseline' },
          { label: 'Current Month', value: 'Current_Month' },
          { label: 'Employees on project', value: 'Employees_On_Project' },
          { label: 'Employee Label', value: 'Employee_Label' }
        ]

        this.dynamicColumns.forEach(col => {
          columns.push({ label: col, value: col })
        })

        const content = this.kpiData.map(item => {
          const selectedLabel = this.employeeLabels.find(label => label.Label_ID === item.Selected_Label_ID)

          const row = {
            Project: '',
            Applicability: item.Is_Active ? 'Yes' : 'No',
            Selector: item.Selector_Name || '',
            Baseline: item.Baseline ? `${item.Baseline}%` : '0%',
            Current_Month: this.displayCurrentMonth(item),
            Employees_On_Project: item.EmployeesOnProject || 0,
            Employee_Label: selectedLabel ? selectedLabel.Label_Name : ''
          }

          this.dynamicColumns.forEach(col => {
            row[col] = item[col] || 0
          })

          return row
        })

        const allContent = [projectRow, ...content]

        const data = [
          {
            sheet: 'HR KPI ( ' + this.selectedProjectName.slice(0, 20) + ' )',
            columns: columns,
            content: allContent
          }
        ]

        const settings = {
          fileName: `HR_KPI_${new Date().toISOString().split('T')[0]}`,
          extraLength: 3,
          writeOptions: {}
        }

        xlsx(data, settings)

        this.$store.dispatch('setSuccess', {
          successMsg: 'Data exported successfully'
        })
      } catch (error) {
        console.error('Export error:', error)
        this.$store.dispatch('setError', {
          errorMsg: 'Export failed: ' + error.message
        })
      } finally {
        this.exporting = false
      }
    },

    getLabels () {
      this.axios.get('/api/project_employee_labels', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.labels = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  }
}
</script>

<style scoped>
.human-resources-kpi {
  margin: 0;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--hr-bg);
  color: var(--hr-text);
}

.kpi-table-wrapper {
  flex: 1;
  border: 1px solid var(--hr-border);
  border-radius: 8px;
  margin-bottom: 12px;
  padding-bottom: 0.5px;
  position: relative;
  background: var(--hr-bg);
  overflow-x: auto;
  overflow-y: auto;
}

.table-container {
  width: 100%;
  height: 100%;
}

.main-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  min-width: 100%;
}

.main-table th,
.main-table td {
  border-bottom: 1px solid var(--hr-border);
  border-right: 1px solid var(--hr-border);
  height: 48px;
  padding: 0 8px;
  vertical-align: middle;
}

.main-table th {
  background: var(--hr-bg-header);
  font-weight: bold;
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 5;
  color: var(--hr-text);
}

/* Fixed column widths */
.applicability {
  width: 120px;
  min-width: 120px;
  text-align: center;
}

.applicability-checkbox {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.selector {
  width: 380px;
  min-width: 380px;
}

.baseline {
  width: 100px;
  min-width: 100px;
}

.current-month {
  width: 160px;
  min-width: 160px;
}

.current-month-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 38px;
}

.employees {
  width: 160px;
  min-width: 160px;
}

.label-dropdown {
  width: 120px;
  min-width: 120px;
  position: relative;
  top: 1px;
}

.period-col {
  width: 120px;
  min-width: 120px;
}

.text-right-field ::v-deep .v-input__control input {
  text-align: right !important;
}

.text-right-field ::v-deep .v-text-field__suffix {
  justify-content: flex-end;
  padding-left: 4px;
}

.label-dropdown ::v-deep(.v-input__slot) {
  transform: translateY(-5px) !important;
  border-bottom: 1px solid var(--hr-border) !important;
  box-shadow: none !important;
}

.label-dropdown ::v-deep(.v-input__slot):focus-within {
  border-bottom-color: rgba(0, 0, 0, 0.87) !important;
}

.label-dropdown ::v-deep(.v-input--is-disabled .v-input__slot) {
  border-bottom-color: rgba(0, 0, 0, 0.26) !important;
}

.label-dropdown ::v-deep(.v-input__slot)::before,
.label-dropdown ::v-deep(.v-input__slot)::after {
  display: none !important;
}

.label-dropdown .v-select ::v-deep(.v-select__slot) {
  margin-top: 2px !important;
  padding-top: 0 !important;
}

.main-table ::v-deep(input),
.main-table ::v-deep(.v-select__selection),
.main-table ::v-deep(.v-select__selections),
.main-table ::v-deep(.v-input__slot),
.main-table ::v-deep(.v-text-field__slot),
.main-table ::v-deep(.v-select__slot) {
  font-size: 16px !important;
  line-height: 24px !important;
}

.selector .v-text-field ::v-deep(.v-input__slot),
.baseline .v-text-field ::v-deep(.v-input__slot),
.employees .v-text-field ::v-deep(.v-input__slot) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.label-dropdown .v-select ::v-deep(.v-input__control) {
  min-height: 40px;
}

.label-dropdown .v-select ::v-deep(.v-select__selection) {
  font-size: 15px !important;
  line-height: 22px !important;
}

.grey--text {
  color: var(--hr-text-muted) !important;
}

.text-right-field ::v-deep input {
  text-align: right !important;
  color: var(--hr-text);
}

.main-table ::v-deep .v-input__slot,
.main-table ::v-deep input,
.main-table ::v-deep .v-select__selection {
  color: var(--hr-text) !important;
}

.main-table ::v-deep .v-text-field__suffix {
  color: var(--hr-text-muted) !important;
}

.label-dropdown ::v-deep .v-select__selection {
  color: var(--hr-text) !important;
}
</style>
